import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Plus, Search, Download, FileSpreadsheet, FileText, MoreHorizontal, Eye, Trash2,
    Users, UserCheck, Sparkles, Target, X, Building2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import DataTable from "@/components/common/DataTable";
import UserCell from "@/components/common/UserCell";
import StatusBadge from "@/components/common/StatusBadge";
import Can, { useCan } from "@/components/common/Can";
import Forbidden from "@/pages/Forbidden";
import { exportToExcel, exportToPdf } from "@/lib/exports";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

function StatMini({ Icon, label, value, accent }) {
    return (
        <div className="card-elevated p-4">
            <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div>
                    <div className="text-[11.5px] text-muted-foreground">{label}</div>
                    <div className="text-[20px] font-bold leading-tight tracking-tight">{value}</div>
                </div>
            </div>
        </div>
    );
}

function CustomerBadge({ status }) {
    const map = {
        active: { bg: "bg-[hsl(var(--success))]/10", text: "text-[hsl(var(--success))]", label: tr.crm.statuses.active },
        passive: { bg: "bg-muted", text: "text-muted-foreground", label: tr.crm.statuses.passive },
        prospect: { bg: "bg-[hsl(var(--info))]/10", text: "text-[hsl(var(--info))]", label: tr.crm.statuses.prospect },
        lost: { bg: "bg-[hsl(var(--destructive))]/10", text: "text-[hsl(var(--destructive))]", label: tr.crm.statuses.lost },
    };
    const m = map[status] || map.active;
    return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${m.bg} ${m.text}`}>{m.label}</span>;
}

function SegmentBadge({ segment }) {
    if (!segment) return <span className="text-muted-foreground text-[12px]">—</span>;
    const map = {
        strategic: "bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]",
        premium: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
        standard: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
        basic: "bg-muted text-muted-foreground",
    };
    return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ${map[segment] || map.basic}`}>{tr.crm.segments[segment] || segment}</span>;
}

export default function CustomersList() {
    const canView = useCan("crm.view");
    const canCreate = useCan("crm.create");
    const canDelete = useCan("crm.delete");
    const canExport = useCan("crm.export");
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [sort, setSort] = useState("updated_at");
    const [order, setOrder] = useState("desc");
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [status, setStatus] = useState("");
    const [segment, setSegment] = useState("");
    const [type, setType] = useState("");
    const [managerId, setManagerId] = useState("");
    const [managers, setManagers] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, prospects: 0, strategic: 0 });

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize, sort, order };
            if (debounced) params.search = debounced;
            if (status) params.status = status;
            if (segment) params.segment = segment;
            if (type) params.type = type;
            if (managerId) params.account_manager_id = managerId;
            const { data } = await api.get("/customers", { params });
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Müşteri listesi alınamadı.");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, sort, order, debounced, status, segment, type, managerId]);

    useEffect(() => { if (canView) load(); }, [load, canView]);

    useEffect(() => {
        (async () => {
            try {
                const [s, m] = await Promise.all([
                    api.get("/customers/stats/summary"),
                    api.get("/employees", { params: { page_size: 200, role: "account_manager" } }).catch(() => api.get("/employees", { params: { page_size: 200 } })),
                ]);
                setStats(s.data || {});
                setManagers(m.data.items || []);
            } catch (_) { /* ignore */ }
        })();
    }, []);

    const clearFilters = () => {
        setSearch(""); setStatus(""); setSegment(""); setType(""); setManagerId(""); setPage(1);
    };
    const filtersActive = search || status || segment || type || managerId;

    const exportColumns = [
        { header: "Müşteri", key: "name", width: 28 },
        { header: "Tür", key: "type_label", width: 12 },
        { header: "Durum", key: "status_label", width: 12 },
        { header: "Segment", key: "segment_label", width: 14 },
        { header: "Sektör", key: "industry", width: 18 },
        { header: "Telefon", key: "phone", width: 18 },
        { header: "E-Posta", key: "email", width: 28 },
        { header: "Web", key: "website", width: 24 },
    ];

    const exportRows = () => rows.map((r) => ({
        ...r,
        type_label: tr.crm.types[r.type] || r.type,
        status_label: tr.crm.statuses[r.status] || r.status,
        segment_label: tr.crm.segments[r.segment] || r.segment || "—",
    }));

    if (!canView) return <Forbidden />;

    const columns = [
        {
            key: "name", header: tr.crm.columns.name, sortable: true,
            render: (r) => (
                <Link to={`/customers/${r.id}`}>
                    <UserCell
                        name={r.name}
                        subtitle={r.industry || tr.crm.types[r.type]}
                        avatarUrl={r.logo_url}
                    />
                </Link>
            ),
        },
        {
            key: "account_manager", header: tr.crm.columns.manager,
            render: (r) => r.account_manager ? (
                <UserCell name={r.account_manager.full_name} subtitle={r.account_manager.title || "Temsilci"} avatarUrl={r.account_manager.avatar_url} size="sm" />
            ) : <span className="text-muted-foreground text-[12px]">{tr.crm.detail.noManager}</span>,
        },
        {
            key: "segment", header: tr.crm.columns.segment, sortable: true,
            render: (r) => <SegmentBadge segment={r.segment} />,
        },
        {
            key: "status", header: tr.crm.columns.status, sortable: true,
            render: (r) => <CustomerBadge status={r.status} />,
        },
        {
            key: "phone", header: tr.crm.columns.phone,
            render: (r) => <span className="text-[13px] tabular-nums">{r.phone || "—"}</span>,
        },
        {
            key: "updated_at", header: tr.crm.columns.updated, sortable: true,
            render: (r) => <span className="text-[12.5px] text-muted-foreground">{r.updated_at ? new Date(r.updated_at).toLocaleDateString("tr-TR") : "—"}</span>,
        },
        {
            key: "actions", header: tr.common.actions, align: "right",
            render: (r) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted" aria-label="İşlemler" data-testid={`cust-actions-${r.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => navigate(`/customers/${r.id}`)}>
                            <Eye className="mr-2 h-4 w-4" /> {tr.common.details}
                        </DropdownMenuItem>
                        {canDelete && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"
                                    onClick={async () => {
                                        if (!window.confirm(tr.crm.detail.confirmDeleteBody)) return;
                                        try {
                                            await api.delete(`/customers/${r.id}`);
                                            toast.success("Müşteri silindi.");
                                            load();
                                        } catch (e) {
                                            toast.error(e?.response?.data?.detail || "Silinemedi.");
                                        }
                                    }}
                                >
                                    <Trash2 className="mr-2 h-4 w-4" /> {tr.common.delete}
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight">{tr.crm.title}</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">{tr.crm.subtitle}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Can permission="crm.export">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-semibold text-foreground hover:bg-muted" data-testid="btn-export">
                                    <Download className="h-4 w-4" /> Dışa Aktar
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => exportToExcel({ filename: "musteriler", sheetName: "Müşteriler", columns: exportColumns, rows: exportRows() })}>
                                    <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => exportToPdf({ filename: "musteriler", title: "Müşteri Listesi", columns: exportColumns, rows: exportRows() })}>
                                    <FileText className="mr-2 h-4 w-4" /> PDF
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </Can>
                    {canCreate && (
                        <Link to="/customers/new" className="btn-brand h-11" data-testid="btn-new-customer">
                            <Plus className="h-4 w-4" /> {tr.crm.new}
                        </Link>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatMini Icon={Building2} label={tr.crm.totalCustomers} value={stats.total || 0} accent="bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]" />
                <StatMini Icon={UserCheck} label={tr.crm.activeCustomers} value={stats.active || 0} accent="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" />
                <StatMini Icon={Target} label={tr.crm.prospects} value={stats.prospects || 0} accent="bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]" />
                <StatMini Icon={Sparkles} label={tr.crm.strategic} value={stats.strategic || 0} accent="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]" />
            </div>

            <div className="card-elevated p-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="relative md:col-span-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Ad, e-posta, telefon, sektör…"
                            className="h-10 w-full rounded-xl border border-border bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40"
                            data-testid="filter-search"
                        />
                    </div>
                    <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="filter-type">
                        <option value="">{tr.crm.allTypes}</option>
                        <option value="corporate">{tr.crm.types.corporate}</option>
                        <option value="individual">{tr.crm.types.individual}</option>
                    </select>
                    <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="filter-status">
                        <option value="">{tr.crm.allStatuses}</option>
                        {Object.entries(tr.crm.statuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select value={segment} onChange={(e) => { setSegment(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="filter-segment">
                        <option value="">{tr.crm.allSegments}</option>
                        {Object.entries(tr.crm.segments).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select value={managerId} onChange={(e) => { setManagerId(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="filter-manager">
                        <option value="">{tr.crm.allManagers}</option>
                        {managers.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                    </select>
                </div>
                {filtersActive && (
                    <div className="mt-3">
                        <button onClick={clearFilters} className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 text-[12px] hover:bg-muted" data-testid="clear-filters">
                            <X className="h-3 w-3" /> {tr.common.clearFilters}
                        </button>
                    </div>
                )}
            </div>

            <DataTable
                columns={columns} rows={rows} loading={loading}
                total={total} page={page} pageSize={pageSize}
                onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
                sort={sort} order={order} onSortChange={(k, o) => { setSort(k); setOrder(o); }}
            />
        </div>
    );
}
